"use client";

import React, { useState } from "react";
import Link from "next/link";
import { NavLink, NavLinkProps } from "./NavLink";
import {
  HiOutlineCpuChip,
  HiOutlineUsers,
  HiOutlineDocumentText,
  HiOutlineShieldCheck,
  HiOutlineLockClosed,
  HiOutlineArrowRight,
  HiOutlineXMark,
  HiOutlineBars3,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineSquare3Stack3D,
  HiOutlineCloud,
} from "react-icons/hi2";
import { Button } from "./Button";
import Image from "next/image";

const navLinks: NavLinkProps[] = [
  {
    label: "Products",
    dropdownItems: [
      {
        section: "CI RUNNERS",
        icon: HiOutlineCpuChip,
        items: [
          {
            href: "/products/ci/cloud-runners",
            label: "Cloud Runners",
            description: "2x faster GitHub Actions runners",
          },
          {
            href: "/products/ci/arm64-runners",
            label: "ARM64 Runners",
            description: "Graviton 4 powered high performance",
          },
          {
            href: "/products/ci/x86-runners",
            label: "x86-64 Runners",
            description: "Baremetal with NVMe storage",
          },
          {
            href: "/products/ci/macos-runners",
            label: "macOS Runners",
            description: "Apple Silicon and Intel support",
          },
          {
            href: "/products/ci/windows-runners",
            label: "Windows Runners",
            description: "Fast Windows CI/CD",
          },
        ],
      },
      {
        section: "Caching and Builds",
        icon: HiOutlineSquare3Stack3D,
        items: [
          {
            href: "/products/ci/cache",
            label: "Unlimited Caching",
            description: "High-performance cache system",
          },
          {
            href: "/products/ci/docker-layer-caching",
            label: "Docker Layer Cache",
            description: "5-10x faster container builds",
          },
          {
            href: "/products/ci/docker-builders",
            label: "Remote Docker Builders",
            description: "Best in market builders",
          },
        ],
      },
      {
        section: "BYOC",
        icon: HiOutlineCloud,
        items: [
          {
            href: "/products/ci/byoc",
            label: "Bring Your Own Cloud",
            description: "Deploy in your AWS, GCP, Azure",
          },
          {
            href: "/products/ci/byoc/aws",
            label: "BYOC AWS",
            description: "Deploy on Amazon Web Services",
          },
          {
            href: "/products/ci/byoc/gcp",
            label: "BYOC GCP",
            description: "Deploy on Google Cloud Platform",
          },
          {
            href: "/products/ci/byoc/azure",
            label: "BYOC Azure",
            description: "Deploy on Microsoft Azure",
          },
        ],
      },
      {
        section: "ENTERPRISE",
        icon: HiOutlineShieldCheck,
        items: [
          {
            href: "/products/enterprise",
            label: "Enterprise Benefits",
            description: "Advanced features for teams",
          },
          {
            href: "/products/security",
            label: "Security and Access Control",
            description: "Enterprise-grade security",
          },
        ],
      },
    ],
  },
  // {
  //   label: "Compare",
  //   dropdownItems: [
  //     { href: "/compare/github", label: "Github vs WarpBuild" },
  //     {
  //       href: "/compare/buildjet",
  //       label: "Buildjet vs WarpBuild",
  //     },
  //     {
  //       href: "/compare/blacksmith",
  //       label: "Blacksmith vs WarpBuild",
  //     },
  //     {
  //       href: "/compare/ubicloud",
  //       label: "Ubicloud vs WarpBuild",
  //     },
  //   ],
  // },
  {
    href: "/pricing",
    label: "Pricing",
  },
  {
    href: "https://docs.warpbuild.com",
    label: "Docs",
    isExternal: true,
  },
  {
    href: "/blog",
    label: "Blog",
    // isExternal: true,
  },
  {
    label: "Company",
    dropdownItems: [
      { href: "/about", label: "About Us", icon: HiOutlineUsers },
      {
        href: "/legal/privacy",
        label: "Privacy Policy",
        icon: HiOutlineLockClosed,
      },
      {
        href: "/legal/terms",
        label: "Terms of Service",
        icon: HiOutlineDocumentText,
      },
      {
        href: "https://warpbuild.instatus.com",
        label: "Status Page",
        isExternal: true,
        icon: HiOutlineShieldCheck,
      },
    ],
  },
];

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdowns([]);
  };

  const toggleDropdown = (label: string) => {
    setOpenDropdowns((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label]
    );
  };

  // This wrapper function will be passed to both NavLink and Button components
  const handleNavClick = () => {
    closeMenu();
  };

  return (
    <nav className="sticky top-0 py-6 bg-navbar-gradient z-20 backdrop-blur-sm">
      <div className="px-8 lg:px-6 container max-w-navbar mx-auto flex justify-between items-center">
        <Link href="/" className="text-2xl font-bold flex items-center">
          <Image
            src="/images/logo.svg"
            alt="WarpBuild Logo"
            width={180}
            height={120}
            className="mr-2"
            quality={100}
          />
        </Link>
        <div className="hidden lg:flex items-center space-x-2 bg-surface-navbar border border-border-default rounded-lg px-2 py-2">
          {navLinks.map((link, index) => (
            <NavLink key={index} {...link} />
          ))}
        </div>

        <div className="hidden lg:flex items-center space-x-6">
          <Button
            variant="secondary"
            as={Link}
            href="https://cal.com/suryao/start"
            icon={HiOutlineArrowRight}
            openInNewTab={true}
          >
            Schedule a Call
          </Button>
          <Button
            variant="primary"
            as={Link}
            href="https://app.warpbuild.com"
            icon={HiOutlineArrowRight}
            openInNewTab={true}
          >
            Get Started
          </Button>
        </div>

        <div className="lg:hidden">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-text-body-secondary hover:text-text-body focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary"
          >
            <span className="sr-only">Open main menu</span>
            {isMenuOpen ? (
              <HiOutlineXMark
                className="block h-7 w-7 text-white"
                aria-hidden="true"
              />
            ) : (
              <HiOutlineBars3
                className="block h-7 w-7 text-white"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="lg:hidden mt-4 bg-surface-navbar mx-4 border border-border-default rounded-lg">
          <div className="px-2 pt-2 pb-3">
            {navLinks.map((link, index) => (
              <div key={index} className="py-1">
                {link.dropdownItems ? (
                  <div>
                    <button
                      onClick={() => toggleDropdown(link.label!)}
                      className="flex justify-between items-center w-full px-3 py-2 text-sm font-medium text-text-link-nav-default hover:text-text-link-nav-hover hover:bg-surface-link-nav-hover rounded-md transition-colors duration-200"
                    >
                      {link.label!}
                      {openDropdowns.includes(link.label!) ? (
                        <HiOutlineChevronUp className="h-4 w-4" />
                      ) : (
                        <HiOutlineChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    {openDropdowns.includes(link.label!) && (
                      <div className="pl-4 space-y-1 mt-1">
                        {link.dropdownItems[0].section
                          ? // Sectioned layout (Products)
                            link.dropdownItems.map((section) => (
                              <div key={section.section} className="mb-2">
                                <div className="flex items-center pl-3 py-1">
                                  {section.customIcon ? (
                                    <Image
                                      src={section.customIcon}
                                      alt={`${section.section} icon`}
                                      width={16}
                                      height={16}
                                      className="mr-2"
                                    />
                                  ) : (
                                    section.icon && (
                                      <section.icon className="w-4 h-4 mr-2 text-icon-secondary-default" />
                                    )
                                  )}
                                  <span className="text-xs text-text-body-secondary">
                                    {section.section}
                                  </span>
                                </div>
                                {section.items?.map((item) => (
                                  <NavLink
                                    key={`${section.section}-${item.href}`}
                                    href={item.href}
                                    label={item.label}
                                    isExternal={item.isExternal}
                                    className="py-2 lg:py-2 pl-4"
                                    onClick={handleNavClick}
                                  />
                                ))}
                              </div>
                            ))
                          : // Simple layout (Company)
                            link.dropdownItems.map((item) => (
                              <NavLink
                                key={item.href!}
                                href={item.href!}
                                label={item.label!}
                                isExternal={item.isExternal}
                                icon={item.icon}
                                className="py-2 lg:py-2"
                                onClick={handleNavClick}
                              />
                            ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    {...link}
                    className="py-2 lg:py-1"
                    onClick={handleNavClick}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="px-2 pt-2 pb-3 space-x-6">
            <Button
              variant="secondary"
              as={Link}
              href="https://cal.com/suryao/start"
              icon={HiOutlineArrowRight}
              openInNewTab={true}
              onClick={handleNavClick}
            >
              Schedule a Call
            </Button>
            <Button
              variant="primary"
              as={Link}
              href="https://app.warpbuild.com"
              icon={HiOutlineArrowRight}
              openInNewTab={true}
              onClick={handleNavClick}
            >
              Get Started
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
